import { useState } from "react";

import {
  IncludeExcludeSelection,
  ListSelection,
  SelectionTags,
} from "common/components/Filter";
import SearchInputFilter from "common/components/Filter/SearchInputFilter";
import Switcher from "common/components/ui/Switcher";

import useNavigatorFilters from "../../../../../../hooks/useNavigatorFilters";
import TypeaheadValues from "./TypeaheadValues";

import type {
  NavigatorFilterMeta,
  NavigatorFilters,
  SelectedNavigatorFilters,
} from "../../../../../../types";

export default function SubfiltersFilter({
  subFilters,
  label,
  returnedFilters,
  selectedFilters,
  setSelectedFilters,
}: {
  subFilters: NavigatorFilterMeta[];
  label: string;
  returnedFilters: NavigatorFilters;
  selectedFilters: SelectedNavigatorFilters;
  setSelectedFilters: (selectedFilters: SelectedNavigatorFilters) => void;
}) {
  const { updateFilterValue, setFilterValue } = useNavigatorFilters(
    selectedFilters,
    setSelectedFilters,
  );

  const [selectedFilterType, setSelectedFilterType] = useState<string>(
    subFilters[0].type,
  );

  const selectedSubfilter = subFilters.find(
    (subFilter) => subFilter.type === selectedFilterType,
  );

  // the value of the filter object returned from the server
  const returnedFilter = returnedFilters?.find(
    (filter) => filter.type === selectedSubfilter.type,
  );
  // the value of the filter object sent to the server, if it's undefined the filter isn't selected
  const selectedFilter = selectedFilters.find(
    (filter) => filter.type === selectedSubfilter.type,
  );

  const selectedValues = selectedFilter ? selectedFilter.values : [];
  const returnedValues = returnedFilter ? returnedFilter.values : [];

  const renderTagsComponent = (resetInputValue?: () => void) => {
    if (selectedValues.length) {
      const { type, selectionType } = selectedSubfilter;
      return (
        <SelectionTags
          selection={selectedValues}
          labelKey="text"
          onRemoveFilter={(value) => {
            updateFilterValue(type, "clear", value);
            if (resetInputValue) {
              resetInputValue();
            }
          }}
          onFlipFilter={
            selectionType === "includeExclude" &&
            ((value) => {
              updateFilterValue(
                type,
                value.selectionType === "INCLUDED" ? "exclude" : "include",
                value,
              );
            })
          }
        />
      );
    }
  };

  const renderSelectionComponent = (
    options: NavigatorFilters[number]["values"],
    resetInputValue: () => void,
  ) => {
    const { selectionType, type } = selectedSubfilter;
    if (selectionType === "includeExclude") {
      return (
        <IncludeExcludeSelection
          labelKey="text"
          countKey="displayCount"
          options={options}
          renderIcon={() => null}
          onSelect={(updateType, selectedOption) => {
            updateFilterValue(type, updateType, selectedOption);
            resetInputValue();
          }}
        />
      );
    }
    return (
      <ListSelection
        countKey="displayCount"
        renderLabel={({ text }) => text}
        options={options}
        onSelect={(selectedOption) => {
          setFilterValue(type, selectedOption);
          resetInputValue();
        }}
      />
    );
  };

  if ("typeaheadType" in selectedSubfilter) {
    const { typeaheadType } = selectedSubfilter;
    return (
      <SearchInputFilter
        label={label}
        renderTagsComponent={renderTagsComponent}
        renderExtraComponent={(resetInputValue) => (
          <Switcher
            size="sm"
            options={subFilters.map((subfilter) => ({
              value: subfilter.type,
              label: subfilter.label,
            }))}
            selectedOption={selectedFilterType}
            onOptionSelect={(selection) => {
              resetInputValue();
              // reset selected subfilters, since only one type can be selected at a time
              setSelectedFilters(
                selectedFilters.filter(
                  ({ type }) =>
                    !subFilters.find((subFilter) => subFilter.type === type),
                ),
              );
              setSelectedFilterType(selection);
            }}
          />
        )}
      >
        {(query, resetInputValue) => (
          <TypeaheadValues
            query={query}
            typeaheadType={typeaheadType}
            selectedValues={selectedValues}
            returnedValues={returnedValues}
            renderSelectionComponent={(options) =>
              renderSelectionComponent(options, resetInputValue)
            }
          />
        )}
      </SearchInputFilter>
    );
  }
}
